import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { SharedService } from '../shared/services/shared.service';
import { SghPropertyService } from '../services/sghProperty.service';
import { AuthService } from '../services/auth.service';
import { constants } from '../constants';
 
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private sharedService = inject(SharedService);
  private sghPropertyService = inject(SghPropertyService);
  private authService = inject(AuthService);
 
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );
 
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      req.url.includes(this.sghPropertyService.sghAuth.issuer) &&
      !req.url.includes('api/v') &&
      !req.url.includes('check-session/status') &&
      !req.headers.has('Content-Type')
    ) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
        Authorization:
          'Basic ' +
          btoa(
            `${this.sghPropertyService.sghAuth.clientId}:${this.sghPropertyService.sghAuth.clientSecret}`,
          ),
      });
      req = req.clone({ headers });
    }
 
    const accessToken = sessionStorage.getItem(
      constants.storageKeys.accessToken,
    );
 
    if (accessToken) {
      req = this.addAuthenticationToken(req, accessToken);
    }
 
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401 || req.url.includes('oauth/token')) {
          const backendErrors = err.error?.errors;
          const backendMessage = Array.isArray(backendErrors)
            ? backendErrors.join(', ')
            : backendErrors || err.error?.detail;
          const msg =
            backendMessage ||
            (err.status == 403
              ? 'You are not authorized to access this feature'
              : 'Something went Wrong');
          this.sharedService.openSnackBar(msg, false);
          return throwError(() => err);
        }
 
        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap((token) => {
              return next.handle(
                this.addAuthenticationToken(req, token.access_token),
              );
            }),
          );
        }
 
        const accessTokenLastUpdatedAt = this.authService.getValue(
          constants.storageKeys.accessTokenLastUpdatedAt,
        );
        const expiresIn = this.authService.getValue(
          constants.storageKeys.expiresIn,
        );
 
        if (
          accessTokenLastUpdatedAt != null &&
          expiresIn != null &&
          new Date().getTime() - Date.parse(accessTokenLastUpdatedAt) <
            expiresIn * 1000
        ) {
          console.log('token not expired');
          return throwError(() => err);
        }
 
        this.refreshTokenInProgress = true;
        this.refreshTokenSubject.next(null);
 
        const refreshToken$ = this.authService.refreshTokens() as Observable<{
          access_token: string;
          refresh_token: string;
          expires_in: number;
        }>;
 
        return refreshToken$.pipe(
          switchMap((data) => {
            this.authService.setValue(
              constants.storageKeys.accessToken,
              data.access_token,
            );
            this.authService.setValue(
              constants.storageKeys.refreshToken,
              data.refresh_token,
            );
            this.authService.setValue(
              constants.storageKeys.accessTokenLastUpdatedAt,
              String(new Date()),
            );
            this.authService.setValue(
              constants.storageKeys.expiresIn,
              data.expires_in,
            );
 
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(data);
 
            return next.handle(
              this.addAuthenticationToken(req, data.access_token),
            );
          }),
          catchError((error) => {
            this.refreshTokenInProgress = false;
            this.authService.logout();
            return throwError(() => error);
          }),
        );
      }),
    );
  }
 
  private addAuthenticationToken(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + token,
      },
    });
  }
}
